<template>
  <div class="fund-industry page-shell">
    <div class="toolbar">
      <span class="toolbar-label">报告期</span>
      <el-select v-model="selectedPeriod" placeholder="选择报告期" size="small" @change="onPeriodChange">
        <el-option
          v-for="item in periodOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <span v-if="reportDate" class="toolbar-meta">截止日期：{{ reportDate }}</span>
    </div>

    <div ref="tableShell" class="table-shell" v-loading="tableLoading" element-loading-text="加载中...">
      <el-table
        ref="industryTable"
        :data="tableData"
        :height="tableHeight"
        :row-style="{ height: '32px', lineHeight: '32px' }"
        :cell-style="{ padding: '2px 0' }"
        style="width: 100%"
      >
        <el-table-column type="index" label="序号" width="70" />
        <el-table-column prop="industryName" label="行业名称" min-width="220">
          <template slot-scope="{ row }">
            <el-link
              v-if="row.detailUrl"
              :href="detailHref(row.detailUrl)"
              target="_blank"
              rel="noopener noreferrer"
            >{{ row.industryName }}</el-link>
            <span v-else>{{ row.industryName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="fundCount" label="基金家数" width="120" sortable align="right">
          <template slot-scope="{ row }">
            <el-link
              v-if="row.industryCode"
              type="primary"
              :underline="false"
              @click.prevent="openFundDetail(row)"
            >{{ formatInt(row.fundCount) }}</el-link>
            <span v-else>{{ formatInt(row.fundCount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="marketValue" label="持仓市值(万元)" min-width="160" sortable align="right">
          <template slot-scope="{ row }">{{ formatNum(row.marketValue) }}</template>
        </el-table-column>
        <el-table-column prop="netValueRatio" label="占净值比" width="120" sortable align="right">
          <template slot-scope="{ row }">{{ formatPct(row.netValueRatio) }}</template>
        </el-table-column>
        <el-table-column label="相关链接" min-width="120" align="center">
          <template slot-scope="{ row }">
            <el-link
              v-if="row.detailUrl"
              :href="detailHref(row.detailUrl)"
              target="_blank"
              rel="noopener noreferrer"
            >基金明细</el-link>
            <span v-else style="color: #909399">—</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { fetchFundIndustryPeriods, fetchFundIndustryHoldings } from '../utils/fundIndustryApi.js'

const industryCache = new Map()

export default {
  name: 'FundIndustry',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedPeriod: '',
      periodOptions: [],
      tableData: [],
      reportDate: '',
      tableLoading: false,
      loadToken: 0,
      tableHeight: 560,
    }
  },
  watch: {
    visible(val) {
      if (val) this.updateTableHeight()
    },
    reportDate() {
      this.updateTableHeight()
    },
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  created() {
    this.initPeriods()
  },
  methods: {
    async initPeriods() {
      try {
        const periods = await fetchFundIndustryPeriods()
        if (!periods.length) {
          this.$message?.warning('暂无重仓行业数据，请先导入')
          return
        }
        this.periodOptions = periods.map((p) => ({
          label: p.reportDate ? `${p.period}（${p.reportDate}）` : p.period,
          value: p.period,
          reportDate: p.reportDate || '',
        }))
        this.selectedPeriod = this.periodOptions[0].value
        this.loadTableData()
      } catch (err) {
        this.$message?.error(`报告期列表加载失败: ${err.message}`)
      }
    },
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const height = Math.max(240, Math.floor(window.innerHeight - top - 20))
        if (height !== this.tableHeight) this.tableHeight = height
        this.$nextTick(() => {
          this.$refs.industryTable?.doLayout?.()
        })
      })
    },
    onPeriodChange() {
      this.loadTableData()
    },
    async loadTableData() {
      if (!this.selectedPeriod) return
      const opt = this.periodOptions.find((p) => p.value === this.selectedPeriod)
      this.reportDate = opt?.reportDate || ''

      const token = ++this.loadToken
      const cached = industryCache.get(this.selectedPeriod)
      if (cached) {
        this.tableData = cached
        if (!this.reportDate && cached[0]?.reportDate) this.reportDate = cached[0].reportDate
        this.updateTableHeight()
        return
      }

      this.tableLoading = true
      try {
        const rows = await fetchFundIndustryHoldings(this.selectedPeriod)
        if (token !== this.loadToken) return
        industryCache.set(this.selectedPeriod, rows)
        this.tableData = rows
        if (!this.reportDate && rows[0]?.reportDate) this.reportDate = rows[0].reportDate
        this.updateTableHeight()
      } catch (err) {
        if (token === this.loadToken) {
          this.$message?.error(`重仓行业数据加载失败: ${err.message}`)
        }
      } finally {
        if (token === this.loadToken) this.tableLoading = false
      }
    },
    openFundDetail(row) {
      if (!row?.industryCode) return
      const period = encodeURIComponent(this.selectedPeriod || '')
      const code = encodeURIComponent(row.industryCode)
      window.location.hash = `#/fund-industry-detail?period=${period}&code=${code}`
    },
    detailHref(url) {
      if (!url) return '#'
      if (/^https?:/i.test(url)) return url
      return `//fund.stockstar.com${url}`
    },
    formatInt(v) {
      if (v == null || v === '') return '—'
      return Number(v).toLocaleString('zh-CN')
    },
    formatNum(v) {
      if (v == null || v === '') return '—'
      return Number(v).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    formatPct(v) {
      if (v == null || v === '') return '—'
      return `${Number(v).toFixed(2)}%`
    },
  },
}
</script>

<style scoped>
.fund-industry {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  text-align: left;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.toolbar-meta {
  margin-left: 8px;
  font-size: 13px;
  color: #909399;
}

.table-shell {
  min-height: 0;
}
</style>
